<script lang="ts">
    import { onMount } from "svelte";
    import { t } from "../stores/locale";

    // LED definitions for reuse
    const commonLeds = [
        [
            { label: "Power", color: "var(--color-info)", state: "on" },
            { label: "Cycle", color: "var(--color-info)", state: "off" },
        ],
        [
            { label: "", color: "var(--color-success)", state: "blinking" },
            { label: "", color: "var(--color-info)", state: "off" },
        ],
        [
            { label: "", color: "var(--color-danger)", state: "off" },
            { label: "", color: "var(--color-warning)", state: "on" },
        ],
        [
            { label: "Fire", color: "var(--color-danger)", state: "off" },
            { label: "Warn", color: "var(--color-warning)", state: "off" },
        ],
    ];

    const licLeds = [
        [{ label: "Power", color: "var(--color-info)", state: "on" }],
        [{ label: "", color: "var(--color-success)", state: "on" }],
    ];

    const relLeds = [
        [
            { label: "Power", color: "var(--color-info)", state: "on" },
            { label: "Cycle", color: "var(--color-info)", state: "off" },
        ],
        [
            { label: "", color: "var(--color-success)", state: "on" },
            { label: "", color: "var(--color-info)", state: "blinking" },
        ],
        [
            { label: "DI-1", color: "var(--color-warning)", state: "off" },
            { label: "DI-2", color: "var(--color-warning)", state: "on" },
        ],
        [
            { label: "1", color: "var(--color-danger)", state: "on" },
            { label: "2", color: "var(--color-danger)", state: "off" },
        ],
        [
            { label: "3", color: "var(--color-danger)", state: "blinking" },
            { label: "4", color: "var(--color-danger)", state: "off" },
        ],
        [
            { label: "5", color: "var(--color-warning)", state: "on" },
            { label: "6", color: "var(--color-warning)", state: "on" },
        ],
        [
            { label: "7", color: "var(--color-warning)", state: "off" },
            { label: "8", color: "var(--color-warning)", state: "off" },
        ],
    ];

    // Data State
    const systemData: Record<string, any> = {
        "hu-01": {
            id: "HU-01",
            modelName: "LISTMOD-HU",
            modelVariant: "",
            type: "Headunit Controller",
            bus: "Master",
            status: "Active / Online",
            ip: "192.168.1.10",
            uptime: "45 Days, 12 Hours",
            leds: commonLeds,
            ports: [
                { label: "USB", color: "transparent", state: "off" },
                {
                    label: "ETH 2",
                    color: "var(--color-success)",
                    state: "blinking",
                },
                {
                    label: "ETH 1",
                    color: "var(--color-warning)",
                    state: "on",
                },
            ],
        },
        "ci-01": {
            id: "CI-01",
            modelName: "LISTMOD-CI",
            modelVariant: "SEC 20",
            type: "Cableinterface Module",
            bus: 1,
            status: "Monitoring",
            protocol: "XXX",
            bandwidth: "12 Kbps",
            "Total Sensors": 124,
            "Active Sensors": 120,
            "Sensors in Revision": 4,
            leds: commonLeds,
        },
        "com-02": {
            id: "COM-02",
            modelName: "LISTMOD-COM",
            modelVariant: "",
            type: "Communication Module",
            bus: 1,
            status: "Transmitting",
            protocol: "MQTT / TLS",
            bandwidth: "12 Kbps",
            leds: commonLeds,
        },
        "lic-99": {
            id: "LIC-99",
            modelName: "LISTMOD-LIC",
            modelVariant: "",
            type: "License Module",
            bus: 1,
            status: "Valid",
            tier: "Pro Enterprise",
            expires: "2027-12-31",
            leds: licLeds,
        },
        "rel-a": {
            id: "REL-A",
            modelName: "LISTMOD-REL",
            modelVariant: "",
            type: "Relay Module (4-Ch)",
            bus: 2,
            status: "Idle",
            load: "2.4A Total",
            state: "Ch1:ON, Ch2:OFF",
            leds: relLeds,
        },
        "rel-b": {
            id: "REL-B",
            modelName: "LISTMOD-REL",
            modelVariant: "",
            type: "Relay Module (4-Ch)",
            bus: 2,
            status: "Active",
            load: "8.1A Total",
            state: "Ch1:OFF, Ch2:ON",
            leds: relLeds,
        },
        "rel-c": {
            id: "REL-C",
            modelName: "LISTMOD-REL",
            modelVariant: "",
            type: "Relay Module (4-Ch)",
            bus: 2,
            status: "Active",
            load: "8.1A Total",
            state: "Ch1:OFF, Ch2:ON",
            leds: relLeds,
        },
    };

    const moduleRenderOrder = [
        "ci-01",
        "com-02",
        "rel-a",
        "lic-99",
        "rel-b",
        "rel-c",
    ];

    let activeId = "hu-01";

    let viewportElement: HTMLElement;
    let canScrollLeft = false;
    let canScrollRight = true;

    $: activeData = systemData[activeId];
    $: activeIndex = moduleRenderOrder.indexOf(activeId);
    $: baseEntries = activeData
        ? Object.entries(activeData).filter(
              ([k]) =>
                  ![
                      "id",
                      "type",
                      "modelName",
                      "modelVariant",
                      "leds",
                      "ports",
                  ].includes(k),
          )
        : [];
    $: gridEntries =
        activeIndex !== -1
            ? [["Index", `#${activeIndex + 1}`], ...baseEntries]
            : baseEntries;

    function selectItem(id: string) {
        activeId = id;
    }

    function checkScroll() {
        if (!viewportElement) return;
        const { scrollLeft, scrollWidth, clientWidth } = viewportElement;

        canScrollLeft = scrollLeft > 0;
        canScrollRight = Math.ceil(scrollLeft) < scrollWidth - clientWidth;
    }

    function scrollByAmount(amount: number) {
        if (viewportElement) {
            viewportElement.scrollBy({ left: amount, behavior: "smooth" });
        }
    }

    function initDragToScroll(slider: HTMLElement) {
        let isDown = false;
        let startX: number;
        let scrollLeft: number;
        let didDrag = false;

        slider.addEventListener("mousedown", (e) => {
            isDown = true;
            didDrag = false;
            slider.classList.add("dragging");
            startX = e.pageX - slider.offsetLeft;
            scrollLeft = slider.scrollLeft;
        });
        slider.addEventListener("mouseleave", () => {
            isDown = false;
            slider.classList.remove("dragging");
        });
        slider.addEventListener("mouseup", () => {
            isDown = false;
            slider.classList.remove("dragging");
        });
        slider.addEventListener("mousemove", (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - slider.offsetLeft;
            const walk = (x - startX) * 2; // Scroll fast
            if (Math.abs(walk) > 5) didDrag = true;
            slider.scrollLeft = scrollLeft - walk;
        });

        slider.addEventListener(
            "click",
            (e) => {
                if (didDrag) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            },
            true,
        );
    }

    onMount(() => {
        if (viewportElement) {
            initDragToScroll(viewportElement);
            setTimeout(checkScroll, 50);
            window.addEventListener("resize", checkScroll);
        }
    });

    import { onDestroy } from "svelte";
    onDestroy(() => {
        if (typeof window !== "undefined") {
            window.removeEventListener("resize", checkScroll);
        }
    });
</script>

<div class="modules-page">
    <section class="card immersive rail-card-wrapper">
        {#if canScrollLeft}
            <div class="nav-fade left">
                <button
                    class="nav-arrow"
                    on:click={() => scrollByAmount(-200)}
                    aria-label="Scroll left"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        ><polyline points="15 18 9 12 15 6"></polyline></svg
                    >
                </button>
            </div>
        {/if}

        <div
            class="rail-viewport"
            bind:this={viewportElement}
            on:scroll={checkScroll}
        >
            <div class="rail-container">
                <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
                <div
                    class="module headunit {activeId === 'hu-01'
                        ? 'active'
                        : ''}"
                    id="hu-01"
                    on:click={() => selectItem("hu-01")}
                >
                    <div class="module-body hu-body">
                        <div class="led-grid hu-led-grid">
                            {#each systemData["hu-01"].leds as row}
                                <div class="led-row">
                                    {#each row as led, i}
                                        <div
                                            class="led-item {i === 0
                                                ? 'left-col'
                                                : 'right-col'} {led.label &&
                                            led.label.startsWith('DI-')
                                                ? 'label-under'
                                                : ''}"
                                        >
                                            {#if ["Power", "Cycle", "Fire", "Warn"].includes(led.label)}
                                                <div class="led-wrapper">
                                                    <span
                                                        class="led-icon {led.state ===
                                                        'blinking'
                                                            ? 'blinking'
                                                            : ''} {led.state ===
                                                        'off'
                                                            ? 'off'
                                                            : 'on'}"
                                                        style="color: {led.color}; filter: drop-shadow(0 0 4px {led.color});"
                                                    >
                                                        {#if led.label === "Power"}<svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                ><path
                                                                    d="M18.36 6.64a9 9 0 1 1-12.73 0"
                                                                /><line
                                                                    x1="12"
                                                                    y1="2"
                                                                    x2="12"
                                                                    y2="12"
                                                                /></svg
                                                            >{:else if led.label === "Cycle"}<svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                ><polyline
                                                                    points="23 4 23 10 17 10"
                                                                /><path
                                                                    d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"
                                                                /></svg
                                                            >{:else if led.label === "Fire"}<svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                ><path
                                                                    d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"
                                                                /></svg
                                                            >{:else if led.label === "Warn"}<svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                ><path
                                                                    d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                                                                /><line
                                                                    x1="12"
                                                                    y1="9"
                                                                    x2="12"
                                                                    y2="13"
                                                                /><line
                                                                    x1="12"
                                                                    y1="17"
                                                                    x2="12.01"
                                                                    y2="17"
                                                                /></svg
                                                            >{/if}
                                                    </span>
                                                </div>
                                            {:else if i === 0}
                                                {#if led.label}
                                                    <span class="led-label"
                                                        >{led.label}</span
                                                    >
                                                {/if}
                                                <div class="led-wrapper">
                                                    <span
                                                        class="led-dot {led.state ===
                                                        'blinking'
                                                            ? 'blinking'
                                                            : ''} {led.state ===
                                                        'off'
                                                            ? 'off'
                                                            : 'on'}"
                                                        style="background-color: {led.color}; box-shadow: 0 0 4px {led.color};"
                                                    ></span>
                                                </div>
                                            {:else}
                                                <div class="led-wrapper">
                                                    <span
                                                        class="led-dot {led.state ===
                                                        'blinking'
                                                            ? 'blinking'
                                                            : ''} {led.state ===
                                                        'off'
                                                            ? 'off'
                                                            : 'on'}"
                                                        style="background-color: {led.color}; box-shadow: 0 0 4px {led.color};"
                                                    ></span>
                                                </div>
                                                {#if led.label}
                                                    <span class="led-label"
                                                        >{led.label}</span
                                                    >
                                                {/if}
                                            {/if}
                                        </div>
                                    {/each}
                                    {#if row.length === 1}
                                        <div></div>
                                    {/if}
                                </div>
                            {/each}
                        </div>
                        <div class="ports-grid">
                            {#each systemData["hu-01"].ports as port}
                                <div class="port-item">
                                    <span class="port-label">{port.label}</span>
                                    <div
                                        class="port-connector realistic {port.label
                                            .toLowerCase()
                                            .includes('usb')
                                            ? 'usb-c'
                                            : 'ethernet'}"
                                    >
                                        {#if port.label
                                            .toLowerCase()
                                            .includes("usb")}
                                            <!-- Realistic USB-C Port -->
                                            <div class="usb-inner">
                                                <div class="usb-tongue"></div>
                                            </div>
                                        {:else}
                                            <!-- Realistic Ethernet Port -->
                                            <div class="eth-casing">
                                                <div class="eth-port-hole">
                                                    <div class="eth-leds">
                                                        <span
                                                            class="eth-led link-led on"
                                                        ></span>
                                                        <span
                                                            class="eth-led act-led {port.state ===
                                                            'blinking'
                                                                ? 'blinking'
                                                                : ''} {port.state ===
                                                            'off'
                                                                ? 'off'
                                                                : 'on'}"
                                                            style="--led-color: {port.color}"
                                                        ></span>
                                                    </div>
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                            {/each}
                        </div>
                    </div>
                    <div class="module-footer hu-footer">
                        <div class="model-name">
                            LIST<span class="small-mod">MOD</span>-{systemData[
                                "hu-01"
                            ].modelName.split("-")[1]}
                        </div>
                        {#if systemData["hu-01"].modelVariant}
                            <div class="model-variant">
                                {systemData["hu-01"].modelVariant}
                            </div>
                        {/if}
                    </div>
                </div>

                {#each moduleRenderOrder as modId, index}
                    {@const data = systemData[modId]}
                    <div
                        class="module {activeId === modId ? 'active' : ''}"
                        id={modId}
                        on:click={() => selectItem(modId)}
                    >
                        <div class="module-body">
                            <div class="led-grid">
                                {#each data.leds as row}
                                    <div class="led-row">
                                        {#each row as led, i}
                                            <div
                                                class="led-item {i === 0
                                                    ? 'left-col'
                                                    : 'right-col'} {led.label &&
                                                led.label.startsWith('DI-')
                                                    ? 'label-under'
                                                    : ''}"
                                            >
                                                {#if ["Power", "Cycle", "Fire", "Warn"].includes(led.label)}
                                                    <div class="led-wrapper">
                                                        <span
                                                            class="led-icon {led.state ===
                                                            'blinking'
                                                                ? 'blinking'
                                                                : ''} {led.state ===
                                                            'off'
                                                                ? 'off'
                                                                : 'on'}"
                                                            style="color: {led.color}; filter: drop-shadow(0 0 4px {led.color});"
                                                        >
                                                            {#if led.label === "Power"}<svg
                                                                    width="14"
                                                                    height="14"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    stroke-width="2"
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    ><path
                                                                        d="M18.36 6.64a9 9 0 1 1-12.73 0"
                                                                    /><line
                                                                        x1="12"
                                                                        y1="2"
                                                                        x2="12"
                                                                        y2="12"
                                                                    /></svg
                                                                >{:else if led.label === "Cycle"}<svg
                                                                    width="14"
                                                                    height="14"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    stroke-width="2"
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    ><polyline
                                                                        points="23 4 23 10 17 10"
                                                                    /><path
                                                                        d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"
                                                                    /></svg
                                                                >{:else if led.label === "Fire"}<svg
                                                                    width="14"
                                                                    height="14"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    stroke-width="2"
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    ><path
                                                                        d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"
                                                                    /></svg
                                                                >{:else if led.label === "Warn"}<svg
                                                                    width="14"
                                                                    height="14"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    stroke-width="2"
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    ><path
                                                                        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                                                                    /><line
                                                                        x1="12"
                                                                        y1="9"
                                                                        x2="12"
                                                                        y2="13"
                                                                    /><line
                                                                        x1="12"
                                                                        y1="17"
                                                                        x2="12.01"
                                                                        y2="17"
                                                                    /></svg
                                                                >{/if}
                                                        </span>
                                                    </div>
                                                {:else if i === 0}
                                                    {#if led.label}
                                                        <span class="led-label"
                                                            >{led.label}</span
                                                        >
                                                    {/if}
                                                    <div class="led-wrapper">
                                                        <span
                                                            class="led-dot {led.state ===
                                                            'blinking'
                                                                ? 'blinking'
                                                                : ''} {led.state ===
                                                            'off'
                                                                ? 'off'
                                                                : 'on'}"
                                                            style="background-color: {led.color}; box-shadow: 0 0 4px {led.color};"
                                                        ></span>
                                                    </div>
                                                {:else}
                                                    <div class="led-wrapper">
                                                        <span
                                                            class="led-dot {led.state ===
                                                            'blinking'
                                                                ? 'blinking'
                                                                : ''} {led.state ===
                                                            'off'
                                                                ? 'off'
                                                                : 'on'}"
                                                            style="background-color: {led.color}; box-shadow: 0 0 4px {led.color};"
                                                        ></span>
                                                    </div>
                                                    {#if led.label}
                                                        <span class="led-label"
                                                            >{led.label}</span
                                                        >
                                                    {/if}
                                                {/if}
                                            </div>
                                        {/each}
                                        {#if row.length === 1}
                                            <div></div>
                                        {/if}
                                    </div>
                                {/each}
                            </div>
                            {#if data["Total Sensors"]}
                                <div class="sensor-overview">
                                    <div class="sensor-stat">
                                        <span class="stat-label">Total</span>
                                        <span class="stat-value"
                                            >{data["Total Sensors"]}</span
                                        >
                                    </div>
                                    <div class="sensor-stat">
                                        <span class="stat-label">Active</span>
                                        <span class="stat-value active"
                                            >{data["Active Sensors"]}</span
                                        >
                                    </div>
                                    <div class="sensor-stat">
                                        <span class="stat-label">Rev.</span>
                                        <span class="stat-value rev"
                                            >{data["Sensors in Revision"]}</span
                                        >
                                    </div>
                                </div>
                            {/if}
                        </div>
                        <div class="module-footer">
                            <div class="model-name">
                                LIST<span class="small-mod">MOD</span
                                >-{data.modelName.split("-")[1]}
                            </div>
                            {#if data.modelVariant}
                                <div class="model-variant">
                                    {data.modelVariant}
                                </div>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        </div>

        {#if canScrollRight}
            <div class="nav-fade right">
                <button
                    class="nav-arrow"
                    on:click={() => scrollByAmount(200)}
                    aria-label="Scroll right"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        ><polyline points="9 18 15 12 9 6"></polyline></svg
                    >
                </button>
            </div>
        {/if}
    </section>

    {#if activeData}
        <section class="card">
            <h2>{activeData.id} - {activeData.type}</h2>
            <div class="detail-grid">
                {#each gridEntries as [key, value]}
                    <div class="info-card">
                        <div class="info-label">{key}</div>
                        <div class="info-value">{value}</div>
                    </div>
                {/each}
            </div>
        </section>
    {/if}
</div>

<style lang="scss">
    /* Map original hardcoded colors to CSS custom properties from _variables.scss */
    .modules-page {
        display: flex;
        flex-direction: column;
        gap: 24px;
        --border-radius-l: 16px;
        --border-radius-m: 12px;
        --border-radius-s: 8px;
    }

    .rail-card-wrapper {
        position: relative;
    }

    .rail-viewport {
        width: 100%;
        overflow-x: auto;
        padding: 32px 0 0 0;
        cursor: grab;
        user-select: none;
        -webkit-user-select: none;

        /* Hide native scrollbar */
        scrollbar-width: none;
        &::-webkit-scrollbar {
            display: none;
        }

        & :global(.dragging) {
            cursor: grabbing;
        }
    }

    .rail-container {
        display: flex;
        align-items: stretch;
        min-width: max-content;
        padding: 0 32px 32px 32px; /* Restored bottom padding since scrollbar is removed */
        gap: 16px;
    }

    .nav-fade {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 100px;
        display: flex;
        align-items: center;
        pointer-events: none; /* Let clicks pass through the gradient itself */
        z-index: 100;

        &.left {
            left: 0;
            background: linear-gradient(
                to right,
                var(--color-bg-secondary) 20%,
                transparent 100%
            );
            justify-content: flex-start;
            padding-left: 20px;
        }

        &.right {
            right: 0;
            background: linear-gradient(
                to left,
                var(--color-bg-secondary) 20%,
                transparent 100%
            );
            justify-content: flex-end;
            padding-right: 20px;
        }
    }

    .nav-arrow {
        pointer-events: auto; /* Re-enable clicks for the button */
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background-color: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        color: var(--color-text);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;

        &:hover {
            background-color: var(--color-bg-active);
            color: var(--color-primary);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }
    }

    /* Modules Styling */
    .module {
        width: 110px;
        min-height: 150px;
        background: var(--color-bg-secondary);
        border-radius: var(--border-radius-m);
        border: 1px solid var(--color-border);
        display: flex;
        flex-direction: column;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        position: relative;
        z-index: 2;

        &.headunit {
            width: 160px;
            flex-shrink: 0;
        }

        &:hover {
            border-color: var(--color-primary);
            z-index: 3;
        }

        &.active {
            background: var(--color-bg-active);
            border-color: var(--color-primary);
            z-index: 5;
        }
    }

    .module-body {
        padding: 16px 12px;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
    }

    .hu-body {
        align-items: flex-end;
        padding-right: 16px;
        padding-left: 16px;
    }

    /* LEDs and Ports Styling */
    .led-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px 12px;
        width: 100%;
    }

    .hu-led-grid {
        grid-template-columns: auto auto;
        justify-content: flex-end;
    }

    .led-row {
        display: contents;
    }

    .led-item {
        display: flex;
        align-items: center;
        gap: 4px;
        min-height: 14px;
    }

    .label-under {
        flex-direction: column !important;
        justify-content: center !important;
        gap: 2px;
    }

    .label-under.left-col {
        align-items: flex-end !important;
    }

    .label-under.right-col {
        align-items: flex-start !important;
    }

    .label-under .led-label {
        order: 2; /* explicitly put text first */
        margin-bottom: 2px;
        margin-top: 0;
        width: 14px;
        display: flex;
        justify-content: center;
    }

    .label-under .led-wrapper {
        order: 1; /* explicitly put dot second */
    }

    .led-wrapper {
        width: 14px;
        height: 14px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
    }

    .left-col {
        justify-content: flex-end;
    }

    .right-col {
        justify-content: flex-start;
    }

    .led-label {
        font-size: 8px;
        font-weight: 600;
        color: var(--color-text-secondary);
        text-transform: uppercase;
    }

    .led-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        display: inline-block;
    }

    .led-dot.off,
    .led-icon.off {
        opacity: 0.15 !important;
        box-shadow: none !important;
        filter: none !important;
    }

    .blinking {
        animation: blink 1s infinite alternate;
    }

    @keyframes blink {
        0% {
            opacity: 0.2;
        }
        100% {
            opacity: 1;
        }
    }

    .ports-grid {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 16px;
        width: 100%;
    }

    .port-item {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        width: 100%;
    }

    .port-label {
        font-size: 10px;
        font-weight: 700;
        color: var(--color-text-secondary);
        text-align: right;
        flex-grow: 1;
    }

    .port-connector {
        width: 32px;
        height: 20px;
        background-color: var(--color-bg-light);
        border: 1px solid var(--color-border);
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        &.usb-c {
            width: 14px;
            height: 28px;
            border-radius: 6px;
            padding: 4px;
            margin-right: 24px; /* Aligns with the 14px wide left LED column (col0). Grid col1(12)+gap(12)=24px. */

            .usb-inner {
                width: 100%;
                height: 100%;
                background: var(--color-bg-offset);
                border: 1px solid var(--color-border);
                border-radius: 3px;
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;

                .usb-tongue {
                    width: 6px;
                    height: 12px;
                    background: var(--color-bg-secondary);
                    border-radius: 1px;
                    border: 1px solid var(--color-border);
                }
            }
        }

        &.ethernet {
            width: 32px;
            height: 42px;
            background: var(--color-bg-offset);
            border: 1px solid var(--color-border);
            padding: 4px;
            margin-right: 6px; /* Moved slightly to the right. */

            .eth-casing {
                width: 100%;
                height: 100%;
                position: relative;
                display: flex;
                flex-direction: row-reverse;
                justify-content: space-between;
                align-items: center;

                .eth-port-hole {
                    flex-grow: 1;
                    height: 24px;
                    background: var(--color-bg-secondary);
                    border: 1px solid var(--color-border);
                    border-radius: 2px;
                    position: relative;
                    margin-left: 4px; /* Centered more in the casing */

                    .eth-leds {
                        position: absolute;
                        left: 2px;
                        top: 2px;
                        bottom: 2px;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        z-index: 5;
                        pointer-events: none;

                        .eth-led {
                            width: 4px;
                            height: 3px; /* Slightly rectangular like real ones */
                            background: #222;
                            border-radius: 0.5px;

                            &.link-led.on {
                                background: #4caf50;
                            }

                            &.act-led.on {
                                background: var(--led-color, #ffeb3b);
                            }

                            &.act-led.off {
                                background: #222;
                            }

                            &.blinking {
                                animation: blink 0.5s infinite alternate;
                            }
                        }
                    }

                    &::after {
                        content: "";
                        position: absolute;
                        right: 0;
                        top: 50%;
                        transform: translateY(-50%);
                        width: 4px;
                        height: 12px;
                        background: var(--color-border);
                        border-radius: 1px 0 0 1px;
                    }
                }
            }
        }
    }

    /* Sensor Overview Display */
    .sensor-overview {
        margin-top: auto;
        padding: 4px 0 8px 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .sensor-stat {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    .stat-label {
        font-size: 7px;
        font-weight: 700;
        color: var(--color-text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .stat-value {
        font-family: "Roboto Mono", monospace;
        font-size: 10px;
        font-weight: 800;
        color: var(--color-text);
        padding: 0 4px;
        min-width: 20px;
        text-align: right;

        &.active {
            color: var(--color-success);
        }

        &.rev {
            color: var(--color-warning);
        }
    }

    .module-footer {
        height: 44px;
        padding: 8px 10px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        background-color: var(--color-bg-offset);
        border-top: 1px solid var(--color-border);
        border-radius: 0 0 calc(var(--border-radius-m) - 1px)
            calc(var(--border-radius-m) - 1px);

        &.hu-footer {
            align-items: center;
        }
    }

    .model-name {
        font-size: 11px;
        font-weight: 800;
        color: var(--color-text-secondary);
        letter-spacing: 0.5px;
    }

    .small-mod {
        font-size: 8px;
    }

    .model-variant {
        font-size: 10px;
        font-weight: 600;
        color: var(--color-text-secondary);
        opacity: 0.7;
        margin-top: 2px;
    }

    /* Detail Panel */
    .detail-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 20px;
    }

    .info-card {
        background: var(--color-bg-offset);
        padding: 16px 20px;
        border-radius: var(--border-radius-m);
        transition: background 0.2s ease;
        display: flex;
        flex-direction: column;
        gap: 6px;

        &:hover {
            background: var(--color-bg-active);
        }
    }

    .info-label {
        font-size: 12px;
        color: var(--color-text-secondary);
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .info-value {
        font-size: 16px;
        font-weight: 400;
        color: var(--color-text);
    }

    @media (max-width: 768px) {
        .rail-viewport {
            padding: 20px;
        }
        .module {
            width: 90px;
        }
        .headunit {
            width: 140px;
        }
    }
</style>
